import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiWifi, FiDroplet, FiWind, FiTv, FiCoffee, FiBed, FiUsers, FiCheck } = FiIcons;

const Rooms = () => {
  const [ref, inView] = useInView({
    threshold: 0.3,
    triggerOnce: true
  });

  const roomTypes = [
    {
      name: 'Standard Double Room',
      image: 'https://images.unsplash.com/photo-1611892440504-42a792e24d32?w=600&h=400&fit=crop',
      price: '$45',
      capacity: '2 Guests',
      size: '25 m²',
      amenities: ['Air Conditioning', 'Private Bathroom', 'Free Wi-Fi', 'Cable TV', 'Tea/Coffee Maker'],
      description: 'Comfortable and clean accommodation perfect for couples seeking reliable safari basecamp comfort.'
    },
    {
      name: 'Family Room',
      image: 'https://images.unsplash.com/photo-1586023492125-27b2c045efd7?w=600&h=400&fit=crop',
      price: '$65',
      capacity: '4 Guests',
      size: '35 m²',
      amenities: ['Air Conditioning', 'Private Bathroom', 'Free Wi-Fi', 'Cable TV', 'Mini Refrigerator', 'Extra Bedding'],
      description: 'Spacious family accommodation with extra space for safari gear and comfortable rest for the whole family.'
    },
    {
      name: 'Safari Suite',
      image: 'https://images.unsplash.com/photo-1578683010236-d716f9a3f461?w=600&h=400&fit=crop',
      price: '$85',
      capacity: '2 Guests',
      size: '40 m²',
      amenities: ['Premium Air Conditioning', 'Luxury Bathroom', 'Free Wi-Fi', 'Smart TV', 'Coffee Machine', 'Balcony', 'Safari Gear Storage'],
      description: 'Our premium offering with enhanced comfort and dedicated safari equipment storage for the discerning traveler.'
    }
  ];

  const amenityIcons = {
    'Air Conditioning': FiWind,
    'Premium Air Conditioning': FiWind,
    'Private Bathroom': FiDroplet,
    'Luxury Bathroom': FiDroplet,
    'Free Wi-Fi': FiWifi,
    'Cable TV': FiTv,
    'Smart TV': FiTv,
    'Tea/Coffee Maker': FiCoffee,
    'Coffee Machine': FiCoffee,
    'Mini Refrigerator': FiBed,
    'Extra Bedding': FiBed,
    'Balcony': FiUsers,
    'Safari Gear Storage': FiBed
  };

  return (
    <section id="rooms" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div
          ref={ref}
          initial={{ opacity: 0, y: 50 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="text-4xl md:text-5xl font-playfair font-bold text-gray-900 mb-6">
            Real Rooms, Real <span className="gradient-text">Comfort</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto font-inter">
            Choose from our carefully designed accommodations, each offering the perfect 
            balance of comfort and functionality for your safari adventure.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-8">
          {roomTypes.map((room, index) => (
            <motion.div
              key={room.name}
              initial={{ opacity: 0, y: 50 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: index * 0.2 }}
              className="glass-effect rounded-3xl overflow-hidden hover-lift group"
            >
              {/* Image */}
              <div className="relative overflow-hidden">
                <img
                  src={room.image}
                  alt={room.name}
                  className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                />
                <div className="absolute top-4 right-4 glass-effect-dark px-3 py-1 rounded-full">
                  <span className="text-white font-semibold">{room.price}/night</span>
                </div>
              </div>

              {/* Content */}
              <div className="p-6">
                <h3 className="text-2xl font-playfair font-bold text-gray-900 mb-2">
                  {room.name}
                </h3>
                
                <div className="flex items-center space-x-4 mb-4 text-sm text-gray-600">
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiUsers} className="w-4 h-4" />
                    <span>{room.capacity}</span>
                  </div>
                  <div className="flex items-center space-x-1">
                    <SafeIcon icon={FiBed} className="w-4 h-4" />
                    <span>{room.size}</span>
                  </div>
                </div>

                <p className="text-gray-600 mb-6 font-inter text-sm leading-relaxed">
                  {room.description}
                </p>

                {/* Amenities */}
                <div className="space-y-2 mb-6">
                  {room.amenities.map((amenity) => (
                    <div key={amenity} className="flex items-center space-x-2">
                      <SafeIcon 
                        icon={amenityIcons[amenity] || FiCheck} 
                        className="w-4 h-4 text-orange-500" 
                      />
                      <span className="text-gray-700 text-sm font-inter">{amenity}</span>
                    </div>
                  ))}
                </div>

                {/* Book Button */}
                <motion.button
                  className="w-full bg-gradient-to-r from-orange-600 to-red-600 text-white py-3 rounded-xl font-semibold ripple-effect"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Book This Room
                </motion.button>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Special Offer Banner */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={inView ? { opacity: 1, scale: 1 } : {}}
          transition={{ duration: 0.8, delay: 0.6 }}
          className="mt-16 liquid-gradient rounded-3xl p-8 text-center text-white"
        >
          <h3 className="text-3xl font-playfair font-bold mb-4">
            The Ranger Package - Exclusive Direct Booking Offer
          </h3>
          <p className="text-xl mb-6 opacity-90">
            Room + Breakfast + Morning Safari with Niluka Expert Guides
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
            <span className="text-2xl font-bold">Starting from $75/person</span>
            <motion.button
              className="bg-white text-orange-600 px-8 py-3 rounded-full font-semibold hover-lift"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Book Ranger Package
            </motion.button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Rooms;